/**
 * HOW MUCH MONEY MAY ACTUALLY MOVE ONTO A COST. One rule, for every screen that
 * asks. (ZEN-190 + ZEN-198)
 *
 * WHAT KEPT GOING WRONG. Four places in the app answered this question and none
 * of them agreed:
 *
 *   · the Overview's "ready to withdraw now" figure added up the whole of every
 *     expense, so it promised money the next screen would refuse (ZEN-198);
 *   · the distribution edit dialog worked out a ceiling of its own (ZEN-190);
 *   · the "Tie eligible expenses" panel worked out a second one;
 *   · and the edit dialog's "US$x left" badge quietly used a third, which is why
 *     it advertised headroom on a row that may never take another cent.
 *
 * THE RULE, stated once. What an expense COMES TO is not what may be drawn against
 * it. A cost carried over from a different calendar year, for instance, may keep
 * the amount already matched to it and not a penny more — so its ceiling is that
 * amount, no matter how large the expense is. That rule lives on the server, which
 * is the only place that may own it, and the server publishes its answer per row.
 * Everything here reads that answer. Nothing here re-derives it.
 *
 * WHY A SHARED FILE RATHER THAN A FIX IN EACH SCREEN. Two of these four sites
 * had already been corrected one at a time, and the two that were left were
 * found by review months apart. The disagreement is the defect; patching the
 * places where it shows up leaves the next one to be discovered the same way.
 *
 * ═══ FAIL CLOSED, ALWAYS ═══
 *
 * Every flag below is read as "yes only if the server said yes". An absent flag
 * means NO. This is not defensive habit, it is a deploy fact: the screens reach
 * production before the server does, so for a while this code is talking to a
 * server that has never heard of these questions — and an older server answers
 * `may_increase: true` for a cost paid in another calendar year, because it has
 * no such rule yet. Read as permission that would offer money the save refuses,
 * which fails the WHOLE save on one row and loses every unrelated change in it.
 *
 * ═══ WHAT THE SERVER SENDS, AND WHAT EACH FIELD MEANS ═══
 *
 *   withdraw_usd          the most this distribution may hold on this cost IN
 *                         TOTAL — the expense less whatever OTHER distributions
 *                         already cover. Not "new money".
 *   allocated_to_editing  how much of that this distribution already holds.
 *                         Absent, and zero, when no distribution is being edited.
 *   selectable            may this row be picked at all.
 *   may_increase          may it be given more than it already holds.
 *   may_increase_with_note  may it be given more IF the account owner writes
 *                         down why — the cross-year decision she is allowed to
 *                         record. False for every ordinary row.
 */

/** The smallest share the server treats as real. Matches TIE_MIN_USD on the
 *  server; a half-cent leftover marker is worth nothing on both sides. */
export const TIE_MIN_USD = 0.005;

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const round2 = (v) => Math.round((num(v) + Number.EPSILON) * 100) / 100;

/** What this distribution already holds against this cost. */
export function heldUsd(item) {
  return round2(item && item.allocated_to_editing);
}

/**
 * The arithmetic alone: expense-room less what is already held, NO RULES APPLIED.
 *
 * DELIBERATELY NOT EXPORTED. Every question a screen actually has is one of the
 * ones below, and each applies a rule to this number before answering. A screen
 * that could reach the bare arithmetic would be a screen that could show a
 * figure the server will not honour, which is the whole family of defects this
 * file closes.
 */
function roomOnTheBillUsd(item) {
  if (!item) return 0;
  return round2(num(item.withdraw_usd) - heldUsd(item));
}

/**
 * HOW MUCH MORE MAY MOVE ONTO THIS COST RIGHT NOW, with nothing further asked
 * of the account owner. Zero unless the server said yes to both questions.
 */
export function newMoneyUsd(item) {
  if (!item) return 0;
  if (item.selectable !== true) return 0;
  if (item.may_increase !== true) return 0;
  const room = roomOnTheBillUsd(item);
  return room > TIE_MIN_USD ? room : 0;
}

/**
 * HOW MUCH MORE COULD MOVE ONTO IT IF SHE RECORDS A REASON.
 *
 * Deliberately NOT gated on `selectable`: the rows this applies to are exactly
 * the ones the ordinary rules refuse, which is what the note is for. The server
 * answers this question having already applied the rule the save will apply, so
 * a `true` here is a promise the save will honour.
 */
export function newMoneyWithNoteUsd(item) {
  if (!item) return 0;
  if (item.may_increase_with_note !== true) return 0;
  const room = roomOnTheBillUsd(item);
  return room > TIE_MIN_USD ? room : 0;
}

/**
 * HOW MUCH MORE MAY GO ONTO THIS COST BY WHATEVER ROUTE IS OPEN — plainly, or
 * with a reason recorded. Zero when neither route is.
 *
 * THE FIGURE THE SCREENS PUT NEXT TO A TICK BOX, and the one that decides
 * whether a row is offered at all. It has to cover both routes: reading only the
 * plain one empties the cross-year list, which is a whole feature disappearing;
 * reading only the note one offers nothing on an ordinary expense. Getting this
 * wrong in the first direction was caught here before it shipped.
 */
export function addableUsd(item) {
  return Math.max(newMoneyUsd(item), newMoneyWithNoteUsd(item));
}

/**
 * THE MOST THIS ROW MAY BE SENT BACK FOR — what it holds, plus whatever more it
 * is allowed. This is the figure a per-row input must be capped at, because the
 * amount the screen sends is a TOTAL and not an addition.
 *
 * WHY THE ANSWER MOVES MONEY, not just the display. The save writes the figure
 * it is given. Too high and the server refuses the whole request, losing every
 * unrelated change in the same save. Too low and it rewrites an existing match
 * DOWNWARD, because the writer cannot tell a cap from a request — deleted tax
 * evidence, under a success message.
 */
export function ceilingUsd(item) {
  if (!item) return 0;
  return round2(heldUsd(item) + addableUsd(item));
}

/**
 * MAY THIS ROW BE KEPT AS IT IS AND NOT GROWN?
 *
 * The question the screen needs in order to stop lying. A row in this state was
 * being drawn with an editable box and a badge reading "US$1,500.00 left" beside
 * an expense it may never take another penny of. Saying "fixed at what it holds, and
 * here is why" is the whole of ZEN-190's remaining half.
 *
 * A row holding nothing that may take nothing is NOT in this state — there is
 * no figure to fix, it simply is not on offer, and the lists already leave it
 * out.
 */
export function isFixedAtWhatItHolds(item) {
  if (!item) return false;
  if (heldUsd(item) <= TIE_MIN_USD) return false;
  return addableUsd(item) <= TIE_MIN_USD;
}

/**
 * READY TO WITHDRAW RIGHT NOW, across a list of costs. (ZEN-198)
 *
 * The Overview's headline nudge. It must count what the withdrawal screen would
 * actually let her move today and nothing else — two screens in the same app
 * disagreeing about the same money is the whole complaint.
 *
 * WHAT IS DELIBERATELY LEFT OUT, because both are money she cannot simply take:
 *   · an expense not yet paid — that is the separate "you can draw ahead" figure,
 *     and folding it in would overstate what has actually been spent;
 *   · money a cross-year note would unlock — real, but it needs a decision she
 *     has to make and write down, and "ready now" would be a lie about it.
 *
 * ═══ "HAS THIS BEEN INCURRED" IS THE SERVER'S ANSWER, NOT A DATE COMPARISON ═══
 *
 * This filtered on `is_future` AND on the row's date against today, which sounds
 * like the same test said twice and is not. `is_future` is decided on the
 * SERVER, in UTC; the date this screen would compare against is the viewer's
 * LOCAL day. For several hours out of every day those disagree, and a cost dated
 * on the boundary fell out of BOTH figures — not future enough for one, not past
 * enough for the other. Money that simply vanished off the Overview, for anyone
 * west of Greenwich, every evening.
 *
 * So there is one question and the server answers it. Between them the two
 * figures below now cover every row exactly once. Found by adversarial diff
 * review, round 1.
 */
export function readyToWithdrawNowUsd(items) {
  return round2((items || [])
    .filter((it) => it && !it.is_future)
    .reduce((sum, it) => sum + newMoneyUsd(it), 0));
}

/**
 * EXPENSES STILL AHEAD THAT SHE COULD DRAW FOR NOW, up to `throughISO`. (ZEN-198)
 *
 * The other half of the Overview's nudge — "next month's costs total X, time a
 * withdrawal so the money is there before they fall due".
 *
 * IT USED TO BE A SUBTRACTION, and that is what made it wrong. The figure was
 * the server's total of costs already incurred MINUS the ready-now figure —
 * two names for the same set of rows, so the difference was always about
 * nothing and the nudge never appeared at all. Worse, the moment the ready-now
 * figure was corrected to exclude money the withdrawal screen refuses, that
 * difference would have become POSITIVE and been labelled "next month's
 * expenses" — turning a card that showed nothing into one that showed the wrong
 * thing.
 *
 * ASKED DIRECTLY INSTEAD: expenses whose date has not arrived, due by the end of
 * the window, that this app would let her draw for today. An expense falling in the
 * NEXT calendar year is not one of those — the money and the cost have to land
 * in the same year — and the server has already left it out of the answer, so
 * nothing here has to know that rule.
 *
 * NO LOWER BOUND OF ITS OWN. "Still ahead" is `is_future`, decided on the server,
 * and adding "later than today" beside it compares a server date against a local
 * one — see the note on the figure above, and the row that used to disappear
 * between the two of them. `throughISO` is a coarse horizon rather than half of a
 * partition, so a boundary day there shifts one expense in or out at the far edge
 * and can leave no gap.
 */
export function comingUpUsd(items, throughISO) {
  return round2((items || [])
    .filter((it) => it && it.is_future)
    .filter((it) => String(it.transaction_date || "").slice(0, 10) <= throughISO)
    .reduce((sum, it) => sum + newMoneyUsd(it), 0));
}

/**
 * HOW MANY DOLLARS OF THIS WITHDRAWAL ARE STILL UNSPENT. (ZEN-380)
 *
 * THE SECOND CEILING, AND UNTIL NOW ONLY ONE SCREEN KNEW ABOUT IT. Every
 * question above answers "how much more will this COST take", which is the
 * expense's side of the arithmetic. There is a second side — how much money
 * actually came out of the plan — and a tie that ignores it records a
 * withdrawal as justifying more than it distributed. That figure is what a
 * family reads to decide whether they have over-drawn, so it has to be
 * impossible rather than merely unusual.
 *
 * The withdrawal dialog has always done this subtraction by hand while it
 * builds a withdrawal. The tie panel never did, and had no way to: it is handed
 * a withdrawal and a list of costs and nothing joined the two. One definition
 * here, so the two screens cannot answer it differently.
 *
 * NEVER NEGATIVE. A record that is already over-matched has no room to give,
 * and reporting a negative would let the caps below hand back nonsense.
 */
export function roomLeftOnWithdrawalUsd(withdrawal) {
  if (!withdrawal) return 0;
  const room = round2(num(withdrawal.amount) - num(withdrawal.allocated_total));
  return room > 0 ? room : 0;
}

/**
 * WHAT TICKING THIS COST WOULD ACTUALLY MOVE, given what is already ticked.
 *
 * The smaller of the two ceilings: what the cost still needs, and what the
 * withdrawal still has. This is exactly the arithmetic the account owner
 * described — tick costs until the withdrawal is fully allocated, and the last
 * one takes only what is left, with its balance staying on the expense as still
 * to be reimbursed.
 *
 * RETURNS THE NEW MONEY, NOT THE TOTAL. The save sends a TOTAL (see the note in
 * AllocPicker), so the caller adds what the row already holds. Keeping this
 * function to "new money" matches every other figure in this file and the one
 * on screen beside the tick box.
 */
export function grantUsd(item, roomLeftOnWithdrawal) {
  const want = addableUsd(item);
  const have = round2(roomLeftOnWithdrawal);
  const give = Math.min(want, have > 0 ? have : 0);
  return give > TIE_MIN_USD ? round2(give) : 0;
}

/**
 * THE SCHOOL'S ALLOWANCE AS A SECOND CEILING ON THE AUTOMATIC FILL. (ZEN-842)
 *
 * Maryse, ZEN-818, 1 Nov 2026: "Yes, stop at allowance". `room` is the
 * server's `allowance_room` from the eligible list: per school, what the
 * allowance leaves for this withdrawal. Returns a function that, asked for an
 * amount on one listed expense, gives back what the fill may take and counts
 * it off that school's room: the same "smaller of the two ceilings" as
 * grantUsd, with this file's rounding. Expenses not held to an allowance take
 * what they asked for. Ticking a row by hand never asks this. Kept here, beside
 * the other ceilings, so there is one place for this arithmetic (Maryse,
 * 4 Nov 2026: "I do not want duplicate processes").
 */
export function allowanceRoomTracker(room) {
  const left = { ...(room || {}) };
  return (it, want) => {
    const s = it && it.allowance_school;
    if (!s || !(s in left)) return want;
    const take = Math.max(0, Math.min(num(want), left[s]));
    left[s] = round2(left[s] - take);
    return round2(take);
  };
}

/** How much room each school still has once these amounts are ticked — for the
 *  "pay the rest outside the 529" link beside rent the fill left out. */
export function roomStillLeft(room, items, amts) {
  const left = { ...(room || {}) };
  for (const it of items || []) {
    const s = it && it.allowance_school;
    if (s && s in left) left[s] = round2(left[s] - num(amts?.[it.id]));
  }
  return left;
}
